import React, { useState, useEffect, useRef } from 'react';

import { Clear } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterAbility,
  CharacterSpell,
  CreatureAbility,
  MinionAbility,
  Spell,
  SummonOption2,
  CreatureInstance,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import CharacterMinionsButton from "@dndComponents/character/minions/CharacterMinionsButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CreatureInstanceInput from "@dndComponents/creature/CreatureInstanceInput";
import { SpellLevel } from '@dndModels/Types';

interface Props {
  char: Character;
  disabled?: boolean;
  shared: boolean;
}

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export default function CharacterMinions({ 
  char,
  disabled,
  shared,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [minion, setMinion] = useState<CreatureInstance | null>(null);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = () => {
    console.log("Minions");
    setReloading(true);
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      char.subscription_holder.subscribe("Minions", mounted, recalculated);
    }
  });
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderMinion = (the_minion: CreatureInstance) => {
    const enabled = api.user && (char.owner_id === api.user._id || (char.campaign && char.campaign.owner_id === api.user._id));
    const name = the_minion.name;
    console.log(the_minion);
    return (
      <span key={the_minion.true_id} style={{ 
        borderTop: "1px solid",
        width: "100%"
      }}>
        <Grid container spacing={0} direction="row" >
          <Grid size={12}>
            <div className="clickable"
              style={{ float: "left", paddingLeft: "4px" }}
              onClick={() => {
                setMinion(the_minion);
                setOpen(true);
              }}>
              { name }
            </div>
            { the_minion.image_url !== "" &&
              <>
                <img className="clickable" src={ the_minion.image_url } alt="minion image" 
                  style={{
                    maxWidth: "5em",
                    maxHeight: "5em",
                    border: "1px solid",
                  }} 
                  onClick={() => {
                    setMinion(the_minion);
                    setOpen(true);
                  }}
                />
              </>
            }
            <div style={{ float: "right" }}>
              <Clear className="clickable"
                onClick={() => {
                  if (enabled) {
                    char.spirit.minions = char.minions.filter(o => o.true_id !== the_minion.true_id);
                    char.subscription_holder.recalculate();
                    char.changed();
                    setReloading(true);
                  }
                }} 
              />
            </div>
          </Grid>
          <Grid size={12}
            container
            spacing={0}
            direction="row"
            style={{ 
              margin: "1px",
              width: "80px" 
            }}
            onClick={() => {
              setMinion(the_minion);
              setOpen(true);
            }}>
            <Grid size={5}>
              <div style={{ float: "right" }}>
                { the_minion.hp.current } 
                { the_minion.hp.temp > 0 && <span style={{ color: "blue" }}>+{the_minion.hp.temp}</span> } 
              </div>
            </Grid>
            <Grid size={7}>
              &nbsp;/&nbsp;
                { the_minion.hp.override_max === null ? 
                  the_minion.hp.max 
                : the_minion.hp.override_max > the_minion.hp.max ?
                  <span style={{ color: "green" }}>{ the_minion.hp.override_max }</span> 
                : 
                  <span style={{ color: "red" }}>{ the_minion.hp.override_max }</span> 
                } 
              { the_minion.hp.max_modifier > 0 ? <span style={{ color: "green" }}>+{the_minion.hp.max_modifier}</span> : (the_minion.hp.max_modifier < 0 && <span style={{ color: "red" }}>{the_minion.hp.max_modifier}</span>) }
            </Grid>
          </Grid>
        </Grid>
      </span>
    );
  }

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={ open } 
          // drawer_only
          onClose={() => {
            setOpen(false);
            char.subscription_holder.modalClosed();
          }}>
          { open && renderMinionStatBlock() }
        </ModalOrDrawer>
      </>
    );
  }

  const renderMinionStatBlock = () => {
    if (minion) {
      return (
        <div style={{ 
          height: "100%",
          overflowX: "hidden"
        }}>
          <CreatureInstanceInput
            char={char}
            creature_instance={minion}
            true_id={minion.true_id}
            onChange={() => {
              char.subscription_holder.recalculate();
              char.changed();
            }}
            onSummon={(spell: CharacterAbility | CharacterSpell | Spell | CreatureAbility | MinionAbility, level: SpellLevel, selected_option: SummonOption2) => {
              char.changed();
              char_util.summonMinions({ char: char, spell, level, selected_option }).then(() => {
                char.subscription_holder.recalculate();
                setReloading(true);
              });
            }}
          />
        </div>
      );
    }
    return null;
  }

  return (
    <Grid className="curved_border bg_black_transp">
      <div>
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <CharacterMinionsButton
              char={char}
              disabled={disabled || shared}
            />
          </Grid>
          { char.minions.sort((a,b) => {return a.name.localeCompare(b.name)}).map(minion => {
            return renderMinion(minion);
          })}
        </Grid>
      </div>
      { renderExtras() }
    </Grid>
  );
}
